import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { apiGetState, apiPutState } from "./api";
import { useAuth } from "./auth";
import { AREAS, CASES, FLASHCARDS, questionById } from "./content";

const DEFAULT = {
  lessons: {}, qstats: {}, mistakes: [], mastered: [], flashcards: {}, cardsReviewed: 0,
  cases: [], spots: [], orders: [], matches: [], scenarios: {}, exams: [], answered: 0,
  correct: 0, areaStats: {}, examDate: null, studySeconds: 0, streak: { count: 0, last: null },
  bookmarks: [], notes: [], profile: {},
};

const LEITNER_DAYS = [0, 1, 2, 4, 7, 14];
const DAY = 86400000;
const todayStr = () => new Date().toISOString().slice(0, 10);

const Ctx = createContext({});

export function AppStateProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(DEFAULT);
  const [ready, setReady] = useState(false);
  const hydrated = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    hydrated.current = false;
    setReady(false);
    if (!user) {
      setState(DEFAULT);
      setReady(true);
      return;
    }
    (async () => {
      try {
        const { data } = await apiGetState();
        setState({ ...DEFAULT, ...(data || {}) });
      } catch {
        setState(DEFAULT);
      }
      hydrated.current = true;
      setReady(true);
    })();
  }, [user]);

  useEffect(() => {
    if (!hydrated.current || !user) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      apiPutState(state).catch(() => {});
    }, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [state, user]);

  const update = useCallback((fn) => setState((prev) => fn(prev)), []);

  const recordAnswer = useCallback((q, isCorrect) => {
    update((s) => {
      const prev = s.qstats[q.id] || { attempts: 0, correct: 0, streak: 0 };
      const streak = isCorrect ? prev.streak + 1 : 0;
      const qstats = { ...s.qstats, [q.id]: { attempts: prev.attempts + 1, correct: prev.correct + (isCorrect ? 1 : 0), streak } };
      const a = s.areaStats[q.area] || { answered: 0, correct: 0 };
      const areaStats = { ...s.areaStats, [q.area]: { answered: a.answered + 1, correct: a.correct + (isCorrect ? 1 : 0) } };
      let mistakes = s.mistakes;
      let mastered = s.mastered;
      if (!isCorrect) {
        if (!mistakes.includes(q.id)) mistakes = [...mistakes, q.id];
      } else if (streak >= 2) {
        mistakes = mistakes.filter((id) => id !== q.id);
        if (!mastered.includes(q.id)) mastered = [...mastered, q.id];
      }
      return { ...s, qstats, areaStats, mistakes, mastered, answered: s.answered + 1, correct: s.correct + (isCorrect ? 1 : 0) };
    });
  }, [update]);

  const completeLesson = useCallback((lessonId) => {
    update((s) => ({ ...s, lessons: { ...s.lessons, [lessonId]: { completed: true, at: new Date().toISOString() } } }));
  }, [update]);

  const reviewFlashcard = useCallback((cardId, knew) => {
    update((s) => {
      const cur = s.flashcards[cardId] || { box: 0, due: 0 };
      const box = knew ? Math.min(cur.box + 1, 5) : 0;
      const due = Date.now() + LEITNER_DAYS[box] * DAY;
      return { ...s, flashcards: { ...s.flashcards, [cardId]: { box, due } }, cardsReviewed: s.cardsReviewed + 1 };
    });
  }, [update]);

  const addExamResult = useCallback((id, title, correct, total, answers) => {
    update((s) => ({
      ...s,
      exams: [{ id, title, score: total ? Math.round((correct / total) * 100) : 0, total, correct, date: new Date().toISOString(), answers }, ...s.exams].slice(0, 100),
    }));
  }, [update]);

  const recordScenario = useCallback((id, scores) => {
    update((s) => ({ ...s, scenarios: { ...s.scenarios, [id]: { scores, at: new Date().toISOString() } } }));
  }, [update]);

  const markDone = useCallback((kind, id) => {
    update((s) => (s[kind].includes(id) ? s : { ...s, [kind]: [...s[kind], id] }));
  }, [update]);

  const setExamDate = useCallback((iso) => update((s) => ({ ...s, examDate: iso })), [update]);
  const addStudySeconds = useCallback((sec) => update((s) => ({ ...s, studySeconds: s.studySeconds + sec })), [update]);
  const toggleBookmark = useCallback((lessonId) => update((s) => ({
    ...s, bookmarks: s.bookmarks.includes(lessonId) ? s.bookmarks.filter((b) => b !== lessonId) : [...s.bookmarks, lessonId],
  })), [update]);
  const addNote = useCallback((text) => update((s) => ({ ...s, notes: [{ id: String(Date.now()), text, at: new Date().toISOString() }, ...s.notes] })), [update]);
  const deleteNote = useCallback((id) => update((s) => ({ ...s, notes: s.notes.filter((n) => n.id !== id) })), [update]);
  const setProfile = useCallback((p) => update((s) => ({ ...s, profile: { ...s.profile, ...p } })), [update]);

  const touchStreak = useCallback(() => {
    update((s) => {
      const today = todayStr();
      if (s.streak.last === today) return s;
      const y = new Date(Date.now() - DAY).toISOString().slice(0, 10);
      const count = s.streak.last === y ? s.streak.count + 1 : 1;
      return { ...s, streak: { count, last: today } };
    });
  }, [update]);

  const daysToExam = useCallback(() => {
    if (!state.examDate) return null;
    const diff = Math.ceil((new Date(state.examDate).getTime() - Date.now()) / DAY);
    return diff < 0 ? 0 : diff;
  }, [state.examDate]);

  const readiness = useCallback(() => {
    const knowledge = state.answered > 0 ? state.correct / state.answered : 0;
    let appA = 0, appC = 0;
    Object.entries(state.qstats).forEach(([id, st]) => {
      const q = questionById(id);
      if (q && (q.type === "case" || q.type === "spot")) { appA += st.attempts; appC += st.correct; }
    });
    const application = appA > 0 ? appC / appA : knowledge;
    const cases = CASES.length ? state.cases.length / CASES.length : 0;
    const seen = Object.values(state.flashcards);
    const retention = seen.length ? seen.reduce((s, f) => s + f.box, 0) / (seen.length * 5) : 0;
    const anyActivity = state.answered > 0 || seen.length > 0 || state.cases.length > 0;
    if (!anyActivity) return 0;
    return Math.round((0.3 * knowledge + 0.3 * application + 0.2 * cases + 0.2 * retention) * 100);
  }, [state]);

  const areaAccuracy = useCallback((areaId) => {
    const a = state.areaStats[areaId];
    if (!a || a.answered === 0) return null;
    return Math.round((a.correct / a.answered) * 100);
  }, [state.areaStats]);

  const weakAreas = useCallback(() => AREAS.map((a) => ({ areaId: a.id, pct: areaAccuracy(a.id) })).filter((x) => x.pct !== null).sort((a, b) => a.pct - b.pct), [areaAccuracy]);

  const dueFlashcardIds = useCallback(() => {
    const now = Date.now();
    return FLASHCARDS.filter((c) => { const f = state.flashcards[c.id]; return !f || f.due <= now; }).map((c) => c.id);
  }, [state.flashcards]);

  const masteredCards = useCallback(() => Object.values(state.flashcards).filter((f) => f.box >= 4).length, [state.flashcards]);

  return (
    <Ctx.Provider value={{
      state, ready, recordAnswer, completeLesson, reviewFlashcard, addExamResult, recordScenario, markDone,
      setExamDate, addStudySeconds, toggleBookmark, addNote, deleteNote, setProfile, touchStreak,
      daysToExam, readiness, areaAccuracy, weakAreas, dueFlashcardIds, masteredCards,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAppState = () => useContext(Ctx);
